import React from "react"

interface HelpPopupProps {
    isOpen: boolean
    onClose: () => void
    onWhatsApp: () => void
    isTrackingPage?: boolean
}

const HelpPopup: React.FC<HelpPopupProps> = ({
    isOpen,
    onClose,
    onWhatsApp,
    isTrackingPage = false
}) => {
    if (!isOpen) return null

    const handleWhatsApp = () => {
        onWhatsApp()
        onClose()
    }

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4 animate-fadeIn"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl transform transition-all duration-300 animate-scaleIn relative"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Botão de fechar */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    aria-label="Fechar"
                >
                    <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <div className="text-center">
                    {/* Ícone mais amigável */}
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-secondary-1/20 to-secondary-2/20 mb-6">
                        <svg
                            className="h-8 w-8 text-secondary-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                    </div>

                    {/* Título mais amigável */}
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                        Está com dificuldades?
                    </h3>

                    {/* Mensagem mais empática */}
                    <p className="text-gray-600 mb-8 leading-relaxed">
                        {isTrackingPage
                            ? "Não se preocupe! Às vezes os dados podem demorar um pouco para aparecer no sistema. Nossa equipe está aqui para te ajudar a encontrar sua encomenda."
                            : "Não se preocupe! Nossa equipe está aqui para te ajudar com qualquer dúvida sobre nossos serviços."}
                    </p>

                    {/* Botões mais atrativos */}
                    <div className="space-y-3">
                        <button
                            onClick={handleWhatsApp}
                            className="w-full px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-secondary-1 to-secondary-2 rounded-xl hover:from-secondary-2 hover:to-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-1 transform hover:scale-105 transition-all duration-200 shadow-lg"
                        >
                            <div className="flex items-center justify-center space-x-2">
                                <svg
                                    className="w-5 h-5"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                                </svg>
                                <span>Falar com nossa equipe</span>
                            </div>
                        </button>

                        <button
                            onClick={onClose}
                            className="w-full px-6 py-3 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
                        >
                            {isTrackingPage
                                ? "Tentar novamente"
                                : "Continuar navegando"}
                        </button>
                    </div>

                    {/* Dica adicional */}
                    <p className="text-xs text-gray-400 mt-4">
                        💡 Dica:{" "}
                        {isTrackingPage
                            ? "Verifique se o CPF/CNPJ e número da nota fiscal estão corretos"
                            : "Nossa equipe está sempre disponível para ajudar!"}
                    </p>
                </div>
            </div>
        </div>
    )
}

export default HelpPopup
